import {
    DepartmentReportStatusCount,
    DepartmentReportStatusSummary,
} from "@features/report/tag/types/departmentReportStatusSummary";

export type StatusCountMode = "blocking" | "soft";

const BLOCKING_STATUSES = new Set(["SUBMITTED", "VALIDATING"]);
const SOFT_STATUSES = new Set(["DRAFT", "NEEDS_ATTENTION", "AWAITING_APPROVAL"]);

const SCOPE_ORDER = ["CHURCH", "DISTRICT", "NATION"] as const;

const SCOPE_LABELS: Record<string, string> = {
    CHURCH: "Church",
    DISTRICT: "District",
    NATION: "Nation",
};

export function statusDisplayLabel(statusName: string): string {
    const normalized = statusName.trim().toUpperCase();
    switch (normalized) {
        case "DRAFT":
            return "Draft";
        case "SUBMITTED":
            return "Submitted (waiting to be validated)";
        case "VALIDATING":
            return "Validating";
        case "NEEDS_ATTENTION":
            return "Needs Attention";
        case "AWAITING_APPROVAL":
            return "Awaiting Approval";
        case "APPROVED":
            return "Approved";
        default:
            return statusName
                .toLowerCase()
                .split("_")
                .filter(Boolean)
                .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
                .join(" ");
    }
}

function filterCounts(
    counts: DepartmentReportStatusCount[],
    mode: StatusCountMode
): DepartmentReportStatusCount[] {
    const allowed = mode === "blocking" ? BLOCKING_STATUSES : SOFT_STATUSES;
    return counts
        .filter((c) => c.count > 0 && allowed.has(c.status_name.trim().toUpperCase()))
        .sort((a, b) => a.report_status_id - b.report_status_id);
}

export function formatStatusCountParts(
    counts: DepartmentReportStatusCount[],
    mode: StatusCountMode
): string[] {
    return filterCounts(counts, mode).map(
        (c) => `${c.count} ${statusDisplayLabel(c.status_name)}`
    );
}

export function formatCombinedStatusCounts(
    counts: DepartmentReportStatusCount[],
    mode: StatusCountMode
): string {
    return formatStatusCountParts(counts, mode).join(", ");
}

export function formatScopeStatusCounts(
    countsByScope: DepartmentReportStatusSummary["counts_by_scope"],
    mode: StatusCountMode
): { scope: string; label: string; line: string }[] {
    return SCOPE_ORDER.flatMap((scope) => {
        const parts = formatStatusCountParts(countsByScope[scope] ?? [], mode);
        if (parts.length === 0) return [];
        return [{
            scope,
            label: SCOPE_LABELS[scope] ?? scope,
            line: parts.join(", "),
        }];
    });
}

export function hasSoftLeftoverCounts(counts: DepartmentReportStatusCount[]): boolean {
    return filterCounts(counts, "soft").length > 0;
}

export function buildHardBlockMessages(summary: DepartmentReportStatusSummary): {
    intro: string;
    wait: string;
    progress: string;
} {
    const combined = formatCombinedStatusCounts(summary.counts, "blocking");
    return {
        intro: "This reporting period can’t be closed yet because some department reports are still being validated.",
        wait: "Wait until validation finishes, then try again.",
        progress: `Still in progress: ${combined}`,
    };
}

export function buildSoftConfirmMessages(summary: DepartmentReportStatusSummary): {
    intro: string;
    leftover: string;
    progress: string;
} {
    const combined = formatCombinedStatusCounts(summary.counts, "soft");
    return {
        intro: "This reporting period can be closed.",
        leftover: "Some department reports aren’t approved yet.",
        progress: `Current status: ${combined}`,
    };
}
